// The template the builder's form states RIGHT NOW, in the shape the read side
// speaks.
//
// It exists so the builder's preview IS the Browse card and its rule sentences
// ARE the drawer's: one wording, one card, no second renderer. Nothing here is
// ever sent — `templateRequest` owns the request, and a rule quoted here while
// it is still half-authored is still refused at save.

import type { CustomChallengeBuilderInput, StepBuilderInput } from '../form';
import type { FailedAccountEnd, LiveLimit, StepPassSettings } from '../wire';
import type { SentenceRule, SentenceTemplate } from './headlineRules';
import { RULE_PARAMS_WIRE, toLadder, toRuleParams } from './ruleParamsWire';
import type { RuleBuilderState } from './ruleRegistry';
import { TEMPLATE_SETTLEMENT } from './templateRequest';

/** The builder authors practice challenges: a pass is decided automatically on
 *  closed trades with the account flat, and never waits for the firm. Its
 *  positions keep trading into a next step on the same account. */
export function practicePassSettings(nextFreshAccount: boolean | undefined): StepPassSettings {
  return {
    pass_decision: 'automatic',
    pass_measure: 'closed_trades_when_flat',
    deadline_with_targets_met: 'expire',
    pass_approval: 'not_required',
    after_pass: nextFreshAccount === false ? 'keep_trading' : 'close_and_lock',
  };
}

/** The builder's programs set no limit of their own on one trader. */
export const NO_LIVE_LIMIT: LiveLimit = { max: null, when_reached: null };

/** The identity the preview template carries. It is never saved, never fetched. */
const PREVIEW_TEMPLATE_ID = 'preview';

type PreviewStep = SentenceTemplate['steps'][number];

const text = (raw: string): string | null => raw.trim() || null;

function whole(raw: string): number | null {
  const value = Number(raw.trim());
  return raw.trim() !== '' && Number.isFinite(value) ? value : null;
}

/** Every term the author has actually stated. An unset one is left out rather
 *  than travelling as a blank a sentence would have to read aloud. */
function stated(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

/**
 * The tagged parameters this rule states so far.
 *
 * `toRuleParams` answers only for a COMPLETE rule, because an incomplete one
 * must never be sent. A preview is not a request: while the author is still
 * choosing terms it quotes the number they have typed and leaves the rest out,
 * so the sentence grows with the form instead of appearing at the last click.
 */
function previewParams(rule: RuleBuilderState): { rule_type: string } & Record<string, unknown> {
  const exact = toRuleParams(rule);
  if (exact) return exact;
  const wire = RULE_PARAMS_WIRE[rule.ruleType];
  const value = rule.value.trim();
  const money = wire?.money && value
    ? { [wire.money]: rule.valueMode === 'percent' ? { percent: value } : { amount: value } }
    : {};
  const scalar = wire?.scalar && value ? { [wire.scalar]: value } : {};
  return { ...stated(rule.params), ...money, ...scalar, rule_type: rule.ruleType };
}

function previewRules(rules: RuleBuilderState[]): SentenceRule[] {
  return rules.filter((rule) => rule.enabled).map((rule) => ({
    // The editor row's own identity, so a caller can ask for ONE rule's sentence.
    rule_id: rule.id,
    timing: rule.timing,
    ...(rule.scope.kind === 'account' ? {} : { scope: rule.scope }),
    params: previewParams(rule),
    ladder: toLadder(rule),
    warning_threshold_pct: text(rule.warningThresholdPct),
  }));
}

function previewStep(step: StepBuilderInput, index: number, next: StepBuilderInput | undefined, total: number): PreviewStep {
  const inactive = step.inactivityDays.trim() !== '';
  return {
    step_id: `${PREVIEW_TEMPLATE_ID}-${index}`,
    sequence: index,
    display_name: step.displayName,
    is_terminal: index === total - 1,
    fresh_account: step.freshAccount,
    account_size_override: null,
    profit_target_pct: text(step.profitTargetPct),
    profit_target_abs: text(step.profitTargetAbs),
    min_trading_days: whole(step.minTradingDays),
    min_profitable_days: whole(step.minProfitableDays),
    time_limit_days: whole(step.timeLimitDays),
    trading_day_mode: step.tradingDayMode,
    min_daily_profit: text(step.minDailyProfit),
    consistency_mode: step.consistencyMode || null,
    consistency_pct: text(step.consistencyPct),
    consistency_basis: step.consistencyBasis,
    consistency_remedy: step.consistencyRemedy,
    consistency_window: step.consistencyWindow,
    inactivity_days: whole(step.inactivityDays),
    inactivity_clock_from: inactive ? step.inactivityClockFrom : null,
    inactivity_outcome: inactive ? step.inactivityOutcome : null,
    payout_peak_policy: step.payoutPeakPolicy,
    // The neutral payout policies the request states too (templateRequest).
    payout_availability: { max_request_pct_of_available: null, required_remaining_buffer: null },
    payout_tiers: { tiers: [] },
    payout_approval: { mode: 'automatic' },
    scaling_ladder: null,
    challenge_day_boundary_time: step.challengeDayBoundaryTime,
    challenge_day_boundary_timezone: step.challengeDayBoundaryTimezone,
    ...practicePassSettings(next?.freshAccount),
    rules: previewRules(step.rules),
  };
}

/** What the form says becomes of a failed, never-reset account. */
export function failedAccountEnd(form: CustomChallengeBuilderInput): FailedAccountEnd {
  return form.failedAccountEnd === 'keep_days'
    ? { kind: 'keep_days', days: Number(form.failedAccountDays) }
    : { kind: form.failedAccountEnd };
}

/** The form as a template view. Its `org_id` is the preview's own, so the card
 *  reads it as the author's template without asking who is signed in. */
export function builderToTemplate(form: CustomChallengeBuilderInput): SentenceTemplate {
  return {
    template_id: PREVIEW_TEMPLATE_ID,
    org_id: PREVIEW_TEMPLATE_ID,
    display_name: form.displayName,
    description: text(form.description),
    account_size: form.accountSize,
    challenge_price: form.challengePrice,
    currency: form.currency,
    leverage: whole(form.leverage) ?? 0,
    execution_profile_key: '',
    template_type: 'practice',
    live_limit: NO_LIVE_LIMIT,
    requires_activation: form.requiresActivation,
    activation_price: form.requiresActivation ? text(form.activationPrice) : null,
    profit_split_pct: text(form.profitSplitPct),
    fee_refund_pct: text(form.feeRefundPct),
    billing_period: form.billingPeriod,
    failed_account_end: failedAccountEnd(form),
    firm_key: null,
    product_family: null,
    settlement_mode: TEMPLATE_SETTLEMENT,
    assurance: 'platform_authoritative',
    revision_no: null,
    ruleset_hash: null,
    ruleset_revision_id: null,
    disclosure_hash: null,
    visibility: null,
    created_by_me: true,
    can_edit: true,
    created_at: '',
    updated_at: '',
    reset_policy: { allowed: form.resetAllowed, max_resets: null, use_original_rules: false },
    steps: form.steps.map((step, index) => previewStep(step, index, form.steps[index + 1], form.steps.length)),
  };
}
